import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarCheck, ChevronDown } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeVariant = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.7 + i * 0.12, duration: 0.7, ease: "easeOut" },
  }),
};

const STATS = [
  { value: "24/7", label: "Šlep Služba" },
  { value: "15+", label: "Godina Iskustva" },
  { value: "30 min", label: "Prosečan Dolazak" },
  { value: "100%", label: "Garancija na Rad" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section id="pocetna" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden grain">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="AutoBay servis" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[rgba(9,9,11,0.97)] via-[rgba(9,9,11,0.8)] to-[rgba(227,22,30,0.28)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-transparent to-[rgba(9,9,11,0.7)]" />

      <div className="relative z-10 px-6 lg:px-12 pt-40 pb-10 max-w-7xl mx-auto w-full">
        <motion.div variants={fadeVariant} custom={0} initial="hidden" animate="show"
          className="inline-flex items-center gap-2 border border-red-600/40 bg-red-600/10 text-red-400 text-xs font-semibold tracking-[0.2em] px-4 py-2 rounded-full mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          PREMIJUM AUTO SERVIS & ŠLEP SLUŽBA — SRBIJA
        </motion.div>

        <h1 data-testid="hero-headline" className="font-display uppercase text-white leading-[0.95] text-5xl sm:text-6xl lg:text-8xl max-w-4xl">
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineVariant} custom={0} initial="hidden" animate="show" className="block">
              Profesionalni
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineVariant} custom={1} initial="hidden" animate="show" className="block">
              Auto Servis <span className="text-red-500">&</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span variants={lineVariant} custom={2} initial="hidden" animate="show" className="block text-stroke">
              24/7 Šlep Služba
            </motion.span>
          </span>
        </h1>

        <motion.p variants={fadeVariant} custom={1} initial="hidden" animate="show"
          className="mt-6 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
          Brza dijagnostika, kompletno održavanje vozila i najpouzdaniji transport
          havarisanih i pokvarenih automobila na teritoriji cele Srbije.
        </motion.p>

        <motion.div variants={fadeVariant} custom={2} initial="hidden" animate="show" className="mt-9 flex flex-wrap items-center gap-4">
          <a data-testid="hero-booking-cta" href="#kontakt"
            className="group flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-semibold px-7 py-4 rounded-full transition-all hover:shadow-[0_0_32px_rgba(227,22,30,0.55)] active:scale-[0.97]">
            <CalendarCheck size={18} className="transition-transform group-hover:-translate-y-0.5" />
            Zakaži Servis
          </a>
          <a data-testid="hero-emergency-cta" href="tel:0677775613"
            className="animate-pulse-ring flex items-center gap-2 border border-white/20 hover:border-red-500/60 bg-white/5 hover:bg-red-600/15 backdrop-blur-sm text-white font-semibold px-7 py-4 rounded-full transition-all active:scale-[0.97]">
            <Phone size={18} className="text-red-500" />
            Hitna Šlep Pomoć — 067 777 5613
          </a>
        </motion.div>

        <motion.div variants={fadeVariant} custom={3} initial="hidden" animate="show"
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
          {STATS.map((s, i) => (
            <div key={s.label} data-testid={i === 0 ? "hero-stat-247" : undefined}
              className="bg-[rgba(9,9,11,0.75)] backdrop-blur-md px-6 py-5 hover:bg-[rgba(227,22,30,0.12)] transition-colors">
              <div className="font-display text-3xl text-white">{s.value}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="relative z-10 flex justify-center pb-6">
        <ChevronDown size={22} className="text-zinc-500 animate-bounce" />
      </motion.div>
    </section>
  );
}
